// @TODO: For whatever the reason, the ESLint rules currently set do not like the
//        string.replaceAll() method. It may have something to do with the version
//        of the typescript package that's installed.
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { Injectable } from "@angular/core";
import { DateTime } from "luxon/src/luxon";
import type { UnparseConfig } from "papaparse";
import { unparse } from "papaparse";
import { BehaviorSubject, combineLatest, map, Observable } from "rxjs";
import { DEFAULT_CONFIG } from "../constants";
import type { AddColumnNamesConfig } from "../interfaces";
import type {
    ColumnNamesSubject,
    ConfigSubject,
    DataWithStringifiedValues,
    DefaultData,
    FieldValue,
    GetCVSStream,
    JsonDataSubject,
    RequiredPropertyValues,
    StringOrDate,
    UnparsedRow,
} from "../types";

@Injectable({
    providedIn: "root",
})
export class CsvService {
    private readonly _columnNames: ColumnNamesSubject;
    private readonly _config: ConfigSubject;
    private readonly _jsonData: JsonDataSubject;

    public readonly csv$: Observable<string>;

    constructor() {
        this._columnNames = new BehaviorSubject<ReadonlyArray<string>>([]);
        this._config = new BehaviorSubject<Readonly<UnparseConfig>>(DEFAULT_CONFIG);
        this._jsonData = new BehaviorSubject<ReadonlyArray<DefaultData>>([]);
        this.csv$ = combineLatest([this._jsonData, this._config, this._columnNames]).pipe(
            map((stream: GetCVSStream): Readonly<string> => this.jsonTOCsv(stream))
        );
    }

    public setColumnNames(columnNames: ReadonlyArray<string>): void {
        this._columnNames.next(columnNames);
    }

    public setConfig(config: Readonly<RequiredPropertyValues<UnparseConfig>>): void {
        this._config.next({
            ...this._config.getValue(),
            ...config,
        });
    }

    private addCustomColumnNamesIfNeeded({ csv, config, columnNames }: AddColumnNamesConfig): Readonly<string> {
        const isSetToPrefixCustomColumnNames = (isHeaderEnabled: boolean, totalColumnNames: number): boolean =>
            !isHeaderEnabled && totalColumnNames >= 1;

        if (!isSetToPrefixCustomColumnNames(config.header, columnNames.length)) {
            return csv;
        }

        const headersAsCsv: string = unparse([columnNames], config);
        return `${headersAsCsv}${config.newline}${csv}`;
    }

    private disableHeaderConfigIfColumnNamesAreSet(
        config: Readonly<UnparseConfig>,
        columnNames: ReadonlyArray<string>
    ): Readonly<UnparseConfig> {
        const headerOptionChangeIsNeeded = (isHeaderEnabled: boolean, totalColumnNames: number): boolean =>
            isHeaderEnabled && totalColumnNames >= 1;

        if (headerOptionChangeIsNeeded(config.header, columnNames.length)) {
            return { ...config, header: false };
        }

        return config;
    }

    private isFreshlyUnparsedCsvNull(csv: string, config: Readonly<UnparseConfig>): boolean {
        const columnNamesForRemoval: string = this.getDefaultColumnNamesForRemoval(config.columns);
        const filteredCsvToAssert: string = csv
            .replaceAll(`"`, "")
            .replaceAll(config.delimiter, "")
            .replaceAll(config.newline, "")
            .replaceAll(columnNamesForRemoval, "")
            .trim();
        return filteredCsvToAssert.length === 0;
    }

    public setJSON(jsonData: NonNullable<ReadonlyArray<DefaultData>>): void {
        this._jsonData.next(jsonData);
    }

    private getDefaultColumnNamesForRemoval(defaultColumnNames: ReadonlyArray<string>): string {
        if (!defaultColumnNames) {
            return "";
        }
        return defaultColumnNames.join("").trim();
    }

    private jsonTOCsv([jsonData, config, columnNames]: GetCVSStream): string {
        // @TODO: strictNullChecks needs to be enabled.
        if (jsonData === null || jsonData.length === 0) {
            return "";
        }

        const jsonDataWithStringifiedFieldValues: ReadonlyArray<DataWithStringifiedValues> =
            this.parseDataTableToStringifyFieldValues(jsonData);

        const configWithUpdateHeaderOption: Readonly<UnparseConfig> = this.disableHeaderConfigIfColumnNamesAreSet(config, columnNames);

        // @TODO: An Injection Token needs to be created for the "unparse" method
        //        that comes from the papaparse library, so we can apply
        //        Dependency Inversion Principle (DIP).
        // Reference: https://www.youtube.com/watch?v=9oHY5TllWaU
        // @NOTE: However, it may be prudent to convert this to a cloud function
        //        and/or a customized, tree-shakable library at a later date.
        const csv: string = unparse<DataWithStringifiedValues>([...jsonDataWithStringifiedFieldValues], configWithUpdateHeaderOption);

        if (this.isFreshlyUnparsedCsvNull(csv, config)) {
            return "";
        }

        return this.addCustomColumnNamesIfNeeded({
            csv,
            columnNames,
            config: configWithUpdateHeaderOption,
        });
    }

    private parseDataTableToStringifyFieldValues(data: ReadonlyArray<DefaultData>): ReadonlyArray<DataWithStringifiedValues> {
        return data.map<DataWithStringifiedValues>(
            (row: DefaultData): DataWithStringifiedValues => this.parseRowToStringifyFieldValues(row)
        );
    }

    private parseRowToStringifyFieldValues(row: Readonly<DefaultData>): DataWithStringifiedValues {
        return Object.entries<FieldValue>(row).reduce<DataWithStringifiedValues>(
            (unparsedRow: DataWithStringifiedValues, [column, field]: UnparsedRow): DataWithStringifiedValues => ({
                ...unparsedRow,
                [column]: this.stringifyFieldValue(field),
            }),
            {}
        );
    }

    private stringifyFieldValue(value: FieldValue): string {
        if (typeof value === "boolean" || typeof value === "number") {
            return value.toString();
        }
        if (this.isDate(value)) {
            const date: DateTime = this.toDateTime(value);
            return date.toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS);
        }
        return value;
    }

    private isDate(value: StringOrDate): value is Date | DateTime {
        if (typeof value === "string") {
            return DateTime.fromISO(value).isValid || DateTime.fromRFC2822(value).isValid;
        }
        return DateTime.isDateTime(value) || DateTime.fromJSDate(value).isValid;
    }

    private toDateTime(value: StringOrDate): DateTime {
        if (DateTime.isDateTime(value)) {
            return value;
        }

        if (value instanceof Date) {
            return DateTime.fromJSDate(value);
        }

        return DateTime.fromISO(value);
    }
}
